// frontend/src/pages/dashboards/ShareDialog.jsx
// Phase 4 step 14c-3: the Share dialog (design 10.6), opened from the
// dashboard header by admins and analysts.
//
// Top: the dashboard's links — each working one with its URL, Copy link,
// Copy embed code when it allows embedding, and Revoke (asked inline first);
// stopped ones (revoked, expired) folded underneath. Below: New link — a
// label, an expiry, "Allow embedding", the filters viewers may change, and the
// warning that a link shows every tile's data, private charts included.
// A new link is copied straight away when the browser allows it, and can be
// copied again from the list at any time (design 10.9 choice 1).
//
// Step 14d-3 (design claude/phase4-14d-sharing-access-design.md section 1):
// above the links, "Who can open this dashboard" (People in {organization} or
// Only specific people) and "People" (colleagues named on a restricted
// dashboard, guests invited by email). Restricting a dashboard with working
// links asks first and revokes them (a restricted dashboard is never public,
// brief decision 4); on a restricted dashboard the links section says why it
// offers no new link. The title line sums it up: "People in Acme · 2 guests ·
// 1 public link". Each section shows its own errors and notices.
//
// The rules (what is sent, what is shown, the words) are in shareState.js and
// accessState.js; ShareContent draws one state of the dialog from props so it
// can be checked as static markup; the default export holds the state and the
// requests.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog } from 'radix-ui';
import { AlertTriangle, Check, Code2, Globe, Link2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import {
  addDashboardPerson, createDashboardShare, dashboardAccess, dashboardPeople, dashboardShares, removeDashboardPerson,
  resendDashboardInvite, revokeDashboardShare, setDashboardAccess, updateDashboardPerson,
} from '../../charts/semanticApi';
import {
  EXPIRY_OPTIONS, MAX_LABEL, STATUS_LABELS, activeShares, canMakeMore, createBody, exposureText, filterNote, filtersText, formProblem,
  linksAllowed, listingFromServer, newForm, pastShares, shareFailureMessage, shareLines, todayUtc, toggleFilter,
} from './shareState';
import {
  accessFailureMessage, accessFromServer, addedNotice, classifyEntry, entryProblem, guestBody, levelNotice, levelRequest, memberBody,
  newGuestDraft, removedNotice, resendNotice, shownSuggestions, suggestionsFromServer, summaryLine, toggleGuestFilter,
} from './accessState';
import { FIELD, LABEL, LevelSection, Messages, PeopleSection, SECTION } from './AccessSections';

const CONTENT = 'fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100%-2rem)] max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-[22px] bg-[var(--ui-panel)] p-6 text-[var(--ui-text)] shadow-[0_24px_60px_rgba(0,0,0,0.6)] ring-1 ring-white/5 focus:outline-none';
const STATUS_TONE = {
  active: 'bg-green-500/10 text-green-400 ring-green-500/20',
  expired: 'bg-white/[0.06] text-[var(--ui-text-muted)] ring-white/10',
  revoked: 'bg-white/[0.06] text-[var(--ui-text-muted)] ring-white/10',
  restricted: 'bg-amber-500/10 text-amber-300 ring-amber-500/20',
};
const SEARCH_DELAY_MS = 200;
const NO_SEARCH = { status: 'idle', people: [], truncated: false };

/** Copy text; true when it worked. Falls back to selecting the field and execCommand where the clipboard API is refused. */
export async function copyText(value, field) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) { await navigator.clipboard.writeText(value); return true; }
  } catch { /* fall through */ }
  try {
    if (field) { field.focus(); field.select(); return document.execCommand('copy'); }
  } catch { /* nothing more to try */ }
  return false;
}

function StatusTag({ status }) {
  return <span className={`rounded-md px-1.5 py-0.5 text-[11px] font-medium ring-1 ${STATUS_TONE[status]}`}>{STATUS_LABELS[status]}</span>;
}

function ShareRow({ share, filters, now, fresh, copied, busy, confirming, onCopy, onRevoke, onConfirmRevoke, onCancelRevoke }) {
  const urlRef = useRef(null);
  const name = share.label || 'Link';
  const [made, end, opened] = shareLines(share, now);
  const which = filtersText(share, filters);
  const active = share.status === 'active';
  return (
    <li data-share-id={share.id} className={`rounded-2xl bg-[var(--ui-inset)] p-3.5 ${fresh ? 'ring-1 ring-green-500/40' : ''}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="truncate text-[13px] font-medium">{name}</span>
        <StatusTag status={share.status} />
        {fresh && <span className="text-[11px] text-green-400">New</span>}
      </div>
      {active && share.url && (
        <div className="mt-2.5 flex flex-col gap-2 sm:flex-row sm:items-center">
          <input ref={urlRef} type="text" readOnly value={share.url} aria-label={`Link: ${name}`} onFocus={(e) => e.target.select()}
            className="min-w-0 flex-1 rounded-xl bg-black/30 px-3 py-1.5 font-mono text-[12px] text-[var(--ui-text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30" />
          <div className="flex shrink-0 gap-1.5">
            <Button type="button" variant="secondary" size="sm" onClick={() => onCopy(share, 'url', urlRef.current)}>
              {copied === `url:${share.id}` ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : <Link2 className="h-3.5 w-3.5" aria-hidden="true" />}
              {copied === `url:${share.id}` ? 'Copied' : 'Copy link'}
            </Button>
            {share.embedCode && (
              <Button type="button" variant="secondary" size="sm" onClick={() => onCopy(share, 'embed', null)}>
                {copied === `embed:${share.id}` ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : <Code2 className="h-3.5 w-3.5" aria-hidden="true" />}
                {copied === `embed:${share.id}` ? 'Copied' : 'Copy embed code'}
              </Button>
            )}
          </div>
        </div>
      )}
      <ul className="mt-2 flex flex-col gap-0.5 text-xs text-[var(--ui-text-muted)]">
        <li>{made}</li>
        <li>{end}</li>
        <li>{opened}</li>
        {which && <li>{which}</li>}
        {share.allowEmbed && <li>Can be embedded in other sites</li>}
      </ul>
      {active && !confirming && (
        <div className="mt-2.5">
          <Button type="button" variant="ghost" size="sm" onClick={() => onRevoke(share)} disabled={Boolean(busy)}>Revoke</Button>
        </div>
      )}
      {active && confirming && (
        <div role="alert" className="mt-2.5 rounded-xl bg-red-500/10 p-3 text-xs text-[var(--ui-text)] ring-1 ring-red-500/25">
          <p>Revoke &quot;{name}&quot;? Anyone who opens it will see &quot;This link is not available.&quot; This can&apos;t be undone; make a new link instead.</p>
          <div className="mt-2 flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onCancelRevoke} disabled={busy === `revoke:${share.id}`}>Keep it</Button>
            <Button type="button" variant="secondary" size="sm" onClick={() => onConfirmRevoke(share)} disabled={busy === `revoke:${share.id}`}
              className="text-red-300">
              {busy === `revoke:${share.id}` && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />} Revoke link
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}

function NewLinkForm({ listing, form, setForm, problem, busy, now, onCreate }) {
  const charts = listing.charts;
  return (
    <form className="mt-3 flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); onCreate(); }} aria-label="New link">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={LABEL} htmlFor="share-label">Label (optional)</label>
          <input id="share-label" type="text" value={form.label} maxLength={MAX_LABEL} autoComplete="off" placeholder="Who it's for, e.g. Office screen"
            onChange={(e) => setForm({ ...form, label: e.target.value })} className={FIELD} />
        </div>
        <div>
          <label className={LABEL} htmlFor="share-expiry">Stops working</label>
          <select id="share-expiry" className={FIELD} value={form.expiry} onChange={(e) => setForm({ ...form, expiry: e.target.value })}>
            {EXPIRY_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          {form.expiry === 'date' && (
            <input type="date" aria-label="Last day the link works" value={form.date} min={todayUtc(now)}
              onChange={(e) => setForm({ ...form, date: e.target.value })} className={`${FIELD} mt-2`} />
          )}
        </div>
      </div>

      <label className="flex items-start gap-2.5 text-[13px]">
        <input type="checkbox" checked={form.allowEmbed} onChange={(e) => setForm({ ...form, allowEmbed: e.target.checked })}
          className="mt-0.5 h-4 w-4 accent-white" />
        <span>
          Allow embedding in other sites
          <span className="block text-xs text-[var(--ui-text-muted)]">You get a snippet to paste into a web page. Without it, the link only opens on its own.</span>
        </span>
      </label>

      {listing.filters.length > 0 && (
        <fieldset>
          <legend className={LABEL}>Filters viewers can change</legend>
          <ul className="flex flex-col gap-2">
            {listing.filters.map((f) => (
              <li key={f.id}>
                <label className="flex items-start gap-2.5 text-[13px]">
                  <input type="checkbox" checked={form.filterIds.includes(f.id)} onChange={() => setForm(toggleFilter(form, f.id))}
                    className="mt-0.5 h-4 w-4 accent-white" />
                  <span>
                    {f.label}
                    {filterNote(f) && <span className="block text-xs text-[var(--ui-text-muted)]">{filterNote(f)}</span>}
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-[var(--ui-text-muted)]">
            A filter you leave unticked keeps its starting value and viewers can&apos;t change it. Filters added to the dashboard later also stay at their starting value on this link.
          </p>
        </fieldset>
      )}

      <div className="rounded-xl bg-amber-500/10 p-3 text-xs text-[var(--ui-text)] ring-1 ring-amber-500/25" data-exposure>
        <p className="flex gap-2">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" aria-hidden="true" />
          <span>{exposureText(charts)} The link always shows the dashboard as it is now, including later changes.</span>
        </p>
        {charts.length > 0 && (
          <ul className="mt-2 flex max-h-32 flex-col gap-1 overflow-y-auto pl-5" aria-label="Charts on this dashboard">
            {charts.map((c) => (
              <li key={c.id} className="flex items-center gap-2">
                <span className="truncate">{c.title}</span>
                {c.visibility !== 'public' && <span className="rounded bg-white/[0.08] px-1 text-[10px] uppercase tracking-wide text-[var(--ui-text-muted)]">private</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      {problem && <p className="text-xs text-red-400" role="alert">{problem}</p>}
      <div className="flex justify-end">
        <Button type="submit" size="sm" disabled={Boolean(problem) || Boolean(busy)}>
          {busy === 'create' ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Globe className="h-3.5 w-3.5" aria-hidden="true" />} Make link
        </Button>
      </div>
    </form>
  );
}

function LinksSection({ listing, access, now, busy, copied, confirming, freshId, message, onCopy, onRevoke, onConfirmRevoke, onCancelRevoke }) {
  const working = activeShares(listing.shares);
  const past = pastShares(listing.shares);
  const allowed = linksAllowed(listing);
  const org = access ? access.organization : 'your organization';
  return (
    <section className="mt-6" aria-label="Anyone with the link">
      <h3 className={SECTION}>Anyone with the link</h3>
      {allowed ? (
        <p className="mt-1 text-xs text-[var(--ui-text-muted)]">
          Anyone with a link sees this dashboard and its live data, without signing in. Make one link per audience, so you can revoke one without breaking the others.
        </p>
      ) : (
        <p className="mt-2 rounded-xl bg-white/[0.05] px-3 py-2 text-xs text-[var(--ui-text)]" data-links-restricted>
          Only specific people can open this dashboard, so it has no public links. To make one, choose &ldquo;People in {org}&rdquo; above first.
        </p>
      )}
      <Messages {...message} />
      {working.length === 0 ? (
        allowed && <p className="mt-2 text-xs text-[var(--ui-text-muted)]">No working links. Make one below.</p>
      ) : (
        <ul className="mt-2 flex flex-col gap-2">
          {working.map((s) => (
            <ShareRow key={s.id} share={s} filters={listing.filters} now={now} fresh={s.id === freshId} copied={copied} busy={busy}
              confirming={confirming === s.id} onCopy={onCopy} onRevoke={onRevoke} onConfirmRevoke={onConfirmRevoke} onCancelRevoke={onCancelRevoke} />
          ))}
        </ul>
      )}
      {past.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-[var(--ui-text-muted)] hover:text-[var(--ui-text)]">
            Stopped links ({past.length})
          </summary>
          <ul className="mt-2 flex flex-col gap-2 opacity-80">
            {past.map((s) => <ShareRow key={s.id} share={s} filters={listing.filters} now={now} />)}
          </ul>
        </details>
      )}
    </section>
  );
}

/**
 * One state of the dialog, drawn from props (no requests of its own).
 * status 'loading' | 'error' | 'ready'; listing from shareState.listingFromServer; access from
 * accessState.accessFromServer (null draws the links alone, as before 14d). error: the load failure.
 * people: PeopleSection's `ui`; levelConfirm: { links } while restricting asks first.
 * linkMessage / levelMessage / peopleMessage: { error, notice } shown in their sections.
 */
export function ShareContent({
  status, listing, access = null, error, dirty, form, setForm, busy = '', copied = '', confirming = null, freshId = null, now = Date.now(),
  linkMessage = {}, levelMessage = {}, peopleMessage = {}, levelConfirm = null, people = null,
  onRetry, onCreate, onCopy, onRevoke, onConfirmRevoke, onCancelRevoke, onClose,
  onPickLevel, onConfirmLevel, onCancelLevel, peopleHandlers = {},
}) {
  const problem = form ? formProblem(form, now) : null;
  const ready = status === 'ready' && listing;
  const links = listing ? activeShares(listing.shares).length : 0;
  return (
    <>
      <Dialog.Title className="text-base font-semibold">Share</Dialog.Title>
      <Dialog.Description className="mt-1 text-xs text-[var(--ui-text-muted)]" data-summary>
        {ready && access ? summaryLine(access, links) : 'Choose who can open this dashboard.'}
      </Dialog.Description>

      {dirty && (
        <p className="mt-3 rounded-xl bg-white/[0.05] px-3 py-2 text-xs text-[var(--ui-text)]" data-dirty-note>
          You have unsaved changes. Links and guests see the saved dashboard; save to share them.
        </p>
      )}
      {error && <p className="mt-3 text-xs text-red-400" role="alert">{error}</p>}

      {status === 'loading' && (
        <p className="flex items-center gap-2 py-8 text-xs text-[var(--ui-text-muted)]" role="status">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Loading sharing settings
        </p>
      )}
      {status === 'error' && (
        <div className="flex flex-col items-start gap-2 py-6">
          <Button type="button" variant="secondary" size="sm" onClick={onRetry}>Try again</Button>
        </div>
      )}

      {ready && (
        <>
          {access && (
            <LevelSection access={access} busy={busy} confirm={levelConfirm} message={levelMessage}
              onPick={onPickLevel} onConfirm={onConfirmLevel} onCancel={onCancelLevel} />
          )}
          {access && people && (
            <PeopleSection access={access} charts={listing.charts} now={now} busy={busy} message={peopleMessage} ui={people} {...peopleHandlers} />
          )}

          <LinksSection listing={listing} access={access} now={now} busy={busy} copied={copied} confirming={confirming} freshId={freshId}
            message={linkMessage} onCopy={onCopy} onRevoke={onRevoke} onConfirmRevoke={onConfirmRevoke} onCancelRevoke={onCancelRevoke} />

          {linksAllowed(listing) && (
            <section className="mt-6" aria-label="New link">
              <h3 className={SECTION}>New link</h3>
              {canMakeMore(listing) ? (
                <NewLinkForm listing={listing} form={form} setForm={setForm} problem={problem} busy={busy} now={now} onCreate={onCreate} />
              ) : (
                <p className="mt-2 text-xs text-[var(--ui-text-muted)]">
                  This dashboard has {listing.maxActive} working links, the most it can have. Revoke one to make another.
                </p>
              )}
            </section>
          )}
        </>
      )}

      <div className="mt-6 flex justify-end">
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>Close</Button>
      </div>
    </>
  );
}

/**
 * open, dashboardId, dirty (the page has unsaved changes), onClose(),
 * onShared(bool): whether the dashboard has a working link, after every load and change;
 * onAccess('org' | 'restricted'): the dashboard's level, after every load and change (14d-3).
 */
export default function ShareDialog({ open, dashboardId, dirty = false, onClose, onShared, onAccess }) {
  const [state, setState] = useState({ status: 'loading', listing: null, access: null });
  const [attempt, setAttempt] = useState(0);
  const [form, setForm] = useState(newForm());
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [linkMessage, setLinkMessage] = useState({});
  const [levelMessage, setLevelMessage] = useState({});
  const [peopleMessage, setPeopleMessage] = useState({});
  const [copied, setCopied] = useState('');
  const [confirming, setConfirming] = useState(null);
  const [freshId, setFreshId] = useState(null);
  const [levelConfirm, setLevelConfirm] = useState(null);
  const [entry, setEntry] = useState('');
  const [search, setSearch] = useState(NO_SEARCH);
  const [problem, setProblem] = useState('');
  const [guestDraft, setGuestDraft] = useState(null);
  const [already, setAlready] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [pendingEdit, setPendingEdit] = useState(null);
  const copiedTimer = useRef(null);
  const live = useRef(false);

  const takeLinks = useCallback((body) => {
    const listing = listingFromServer(body);
    setState((s) => ({ ...s, listing }));
    if (onShared) onShared(activeShares(listing.shares).length > 0);
    return listing;
  }, [onShared]);

  const takeAccess = useCallback((body) => {
    const access = accessFromServer(body);
    setState((s) => ({ ...s, access }));
    if (onAccess) onAccess(access.access);
    return access;
  }, [onAccess]);

  useEffect(() => {
    live.current = open;
    if (!open) return undefined;
    const ctrl = new AbortController();
    setState({ status: 'loading', listing: null, access: null });
    setError(''); setLinkMessage({}); setLevelMessage({}); setPeopleMessage({});
    setCopied(''); setConfirming(null); setFreshId(null); setBusy(''); setLevelConfirm(null);
    setEntry(''); setSearch(NO_SEARCH); setProblem(''); setGuestDraft(null); setAlready(null); setRemoving(null); setPendingEdit(null);
    Promise.all([dashboardShares(dashboardId, { signal: ctrl.signal }), dashboardAccess(dashboardId, { signal: ctrl.signal })])
      .then(([linksBody, accessBody]) => {
        if (ctrl.signal.aborted) return;
        const listing = listingFromServer(linksBody);
        const access = accessFromServer(accessBody);
        setState({ status: 'ready', listing, access });
        setForm(newForm(listing.filters));
        if (onShared) onShared(activeShares(listing.shares).length > 0);
        if (onAccess) onAccess(access.access);
      })
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        setState({ status: 'error', listing: null, access: null });
        setError(accessFailureMessage(err, 'load'));
      });
    return () => ctrl.abort();
  }, [open, dashboardId, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  // The Add box's colleagues: asked for as the person types (both levels: on the organization level the
  // answer only tells the box that an address is a colleague's, nothing is listed).
  useEffect(() => {
    const q = entry.trim();
    if (!open || state.status !== 'ready' || !q || guestDraft) { setSearch(NO_SEARCH); return undefined; }
    const ctrl = new AbortController();
    setSearch((s) => ({ ...s, status: 'loading' }));
    const timer = setTimeout(() => {
      dashboardPeople(dashboardId, q, { signal: ctrl.signal })
        .then((body) => { if (!ctrl.signal.aborted) setSearch({ status: 'ready', ...suggestionsFromServer(body) }); })
        .catch(() => { if (!ctrl.signal.aborted) setSearch({ status: 'error', people: [], truncated: false }); });
    }, SEARCH_DELAY_MS);
    return () => { clearTimeout(timer); ctrl.abort(); };
  }, [open, dashboardId, entry, guestDraft, state.status]);

  const flashCopied = (key) => {
    setCopied(key);
    clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopied(''), 2000);
  };

  // --- links (14c-3) -----------------------------------------------------------------------

  const onCopy = async (share, what, field) => {
    const value = what === 'embed' ? share.embedCode : share.url;
    if (!value) return;
    if (await copyText(value, field)) { flashCopied(`${what}:${share.id}`); setLinkMessage({}); } else setLinkMessage({ error: 'Copying was blocked by the browser. Select the link and copy it yourself.' });
  };

  const onCreate = async () => {
    const listing = state.listing;
    if (!listing || busy || formProblem(form)) return;
    setBusy('create'); setLinkMessage({});
    try {
      const next = takeLinks(await createDashboardShare(dashboardId, createBody(form, listing.filters)));
      if (!live.current) return;
      setForm(newForm(next.filters));
      const made = next.created;
      setFreshId(made ? made.id : null);
      const ok = made && made.url ? await copyText(made.url, null) : false;
      if (ok) flashCopied(`url:${made.id}`);
      setLinkMessage({ notice: ok ? 'Link made and copied. You can copy it again from the list above at any time.' : 'Link made. Copy it from the list above.' });
    } catch (err) {
      setLinkMessage({ error: shareFailureMessage(err, 'create') });
    } finally { setBusy(''); }
  };

  const onConfirmRevoke = async (share) => {
    if (busy) return;
    setBusy(`revoke:${share.id}`); setLinkMessage({});
    try {
      takeLinks(await revokeDashboardShare(dashboardId, share.id));
      setConfirming(null);
      setLinkMessage({ notice: `"${share.label || 'Link'}" no longer works.` });
    } catch (err) {
      setLinkMessage({ error: shareFailureMessage(err, 'revoke') });
    } finally { setBusy(''); }
  };

  // --- the level (14d-3) -----------------------------------------------------------------

  const workingLinks = state.listing ? activeShares(state.listing.shares).length : (state.access ? state.access.publicLinks : 0);

  const changeLevel = async (target, revokeLinks) => {
    setBusy('level'); setLevelMessage({});
    const before = workingLinks;
    try {
      const access = takeAccess(await setDashboardAccess(dashboardId, target, { revokeLinks }));
      if (!live.current) return;
      setLevelConfirm(null);
      setLevelMessage({ notice: levelNotice(access, revokeLinks ? before : 0) });
      // Restricting revoked the links; opening it again lets the section offer a new one. Either way, reread them.
      try { takeLinks(await dashboardShares(dashboardId)); } catch { /* the level changed; the links show their last state */ }
    } catch (err) {
      if (err && err.code === 'PUBLIC_LINKS_ACTIVE') {
        // A link was made somewhere else meanwhile: ask, with the server's count, and show it.
        setLevelConfirm({ links: err.links || 1 });
        dashboardShares(dashboardId).then(takeLinks).catch(() => {});
      } else {
        setLevelMessage({ error: accessFailureMessage(err, 'level') });
      }
    } finally { setBusy(''); }
  };

  const onPickLevel = (target) => {
    if (busy || !state.access) return;
    const r = levelRequest(state.access.access, target, workingLinks);
    setLevelMessage({});
    if (r.action === 'confirm') setLevelConfirm({ links: r.links });
    else if (r.action === 'put') { setLevelConfirm(null); changeLevel(target, false); }
  };

  // --- people (14d-3) ----------------------------------------------------------------------

  const resetEntry = () => { setEntry(''); setProblem(''); setGuestDraft(null); setAlready(null); setSearch(NO_SEARCH); };

  const personRequest = async (key, action, request, notice) => {
    if (busy) return null;
    setBusy(key); setPeopleMessage({});
    try {
      const access = takeAccess(await request());
      if (!live.current) return access;
      setPeopleMessage({ notice: notice(access) });
      return access;
    } catch (err) {
      setPeopleMessage({ error: accessFailureMessage(err, action) });
      return err;
    } finally { setBusy(''); }
  };

  const addMember = async (user) => {
    const out = await personRequest('add', 'add', () => addDashboardPerson(dashboardId, memberBody(user)), (a) => addedNotice(a.person));
    if (out && !(out instanceof Error)) resetEntry();
  };

  const onSubmitEntry = () => {
    if (busy || !state.access) return;
    const c = classifyEntry(entry, { suggestions: search.people, access: state.access });
    if (c.kind === 'member') { addMember(c.user); return; }
    if (c.kind === 'guest') { setGuestDraft(newGuestDraft(c.email, state.access.filters)); setProblem(''); setPeopleMessage({}); return; }
    setProblem(entryProblem(c, state.access));
  };

  const onSendGuest = async () => {
    if (busy || !guestDraft || !state.access) return;
    const draft = guestDraft;
    setBusy('add'); setPeopleMessage({});
    try {
      const access = takeAccess(await addDashboardPerson(dashboardId, guestBody(draft.email, draft.filterIds, state.access.filters)));
      if (!live.current) return;
      resetEntry();
      setPeopleMessage({ notice: addedNotice(access.person) });
    } catch (err) {
      if (err && err.code === 'ALREADY_MEMBER') { setGuestDraft(null); setAlready({ email: draft.email, user: err.user }); }
      else setPeopleMessage({ error: accessFailureMessage(err, 'add') });
    } finally { setBusy(''); }
  };

  const onToggleEdit = async (person, value) => {
    if (busy) return;
    setPendingEdit({ id: person.id, value });          // the box moves at once; the server's answer replaces it
    try {
      await personRequest(`edit:${person.id}`, 'edit',
        () => updateDashboardPerson(dashboardId, person.id, { can_edit: value }),
        () => (value ? `${person.email} can edit this dashboard now.` : `${person.email} can open this dashboard but not change it now.`));
    } finally { setPendingEdit(null); }
  };

  const onResend = (person) => personRequest(`resend:${person.id}`, 'resend',
    () => resendDashboardInvite(dashboardId, person.id), (a) => resendNotice(a.person));

  const onConfirmRemove = async (person) => {
    const out = await personRequest(`remove:${person.id}`, 'remove', () => removeDashboardPerson(dashboardId, person.id), () => removedNotice(person));
    if (out && !(out instanceof Error)) setRemoving(null);
  };

  const peopleUi = {
    entry,
    suggestions: { ...search, people: shownSuggestions(search.people, state.access) },
    problem, guestDraft, already, removing, pendingEdit,
  };
  const peopleHandlers = {
    onEntry: (value) => { setEntry(value); setProblem(''); setAlready(null); },
    onSubmitEntry,
    onPickSuggestion: (user) => { if (!user.added) addMember(user); },
    onToggleGuestFilter: (id) => setGuestDraft((d) => (d ? toggleGuestFilter(d, id) : d)),
    onSendGuest,
    onCancelGuest: () => { setGuestDraft(null); setAlready(null); },
    onAddInstead: (user) => addMember(user),
    onAskRemove: (person) => { setRemoving(person.id); setPeopleMessage({}); },
    onConfirmRemove,
    onCancelRemove: () => setRemoving(null),
    onToggleEdit,
    onResend,
  };

  return (
    <Dialog.Root open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content className={CONTENT} data-share-dialog>
          <ShareContent
            status={state.status} listing={state.listing} access={state.access} error={error} dirty={dirty}
            form={form} setForm={setForm} busy={busy} copied={copied} confirming={confirming} freshId={freshId}
            linkMessage={linkMessage} levelMessage={levelMessage} peopleMessage={peopleMessage} levelConfirm={levelConfirm} people={peopleUi}
            onRetry={() => setAttempt((n) => n + 1)} onCreate={onCreate} onCopy={onCopy}
            onRevoke={(s) => { setConfirming(s.id); setLinkMessage({}); }} onConfirmRevoke={onConfirmRevoke} onCancelRevoke={() => setConfirming(null)}
            onClose={onClose}
            onPickLevel={onPickLevel} onConfirmLevel={() => changeLevel('restricted', true)} onCancelLevel={() => setLevelConfirm(null)}
            peopleHandlers={peopleHandlers}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
